/**
 * Recently Viewed Products Module
 * Tracks products in sessionStorage, injects them into the section,
 * then hands slider init off to the theme SliderBlock (assets/slider.js).
 */

class RecentlyViewed {
  constructor() {
    this.storageKey = 'recently_viewed';
    this.maxProducts = this.getMaxProducts();
    this.init();
  }

  /**
   * Get maximum number of products from data attribute
   */
  getMaxProducts() {
    const element = document.querySelector('recently-viewed');
    return parseInt(element?.getAttribute('data-products-count') || '8', 10);
  }

  /**
   * Escape quotes in strings to prevent JavaScript injection
   */
  escapeQuotes(str) {
    if (typeof str !== 'string') return str;
    return str.replace(/'/g, "\\'").replace(/"/g, '\\"');
  }

  /**
   * Escape values used in HTML attributes
   */
  escapeHtml(str) {
    if (typeof str !== 'string') return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /**
   * Get existing recently viewed data from sessionStorage
   */
  getStoredData() {
    try {
      const data = sessionStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Save data to sessionStorage
   */
  saveData(data) {
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(data));
    } catch (error) {
      // Silently handle storage errors
    }
  }

  /**
   * Prefer a stored image URL; fall back to src on legacy stored <img> HTML
   */
  getProductImageUrl(product) {
    if (product?.productImgUrl) return product.productImgUrl;
    if (typeof product?.productImg === 'string') {
      const match = product.productImg.match(/src=(['"])(.*?)\1/);
      if (match) return match[2].replace(/\\(.)/g, '$1');
    }
    return '';
  }

  /**
   * Request a specific width from a Shopify CDN URL
   */
  resizeShopifyImage(url, width) {
    if (!url) return '';
    const decoded = url.replace(/&amp;/g, '&');
    if (/([?&])width=\d+/.test(decoded)) {
      return decoded.replace(/([?&])width=\d+/, `$1width=${width}`);
    }
    return `${decoded}${decoded.includes('?') ? '&' : '?'}width=${width}`;
  }

  /**
   * Add current product to recently viewed list
   */
  addCurrentProduct() {
    if (!pdpData?.productTitle?.trim()) {
      return;
    }

    const currentProduct = {
      productTitle: this.escapeQuotes(pdpData.productTitle.trim()),
      productImgUrl: pdpData.productImgUrl || '',
      productUrl: pdpData.productUrl || '',
    };
    const existingData = this.getStoredData();
    const currentTitle = currentProduct.productTitle;

    const duplicateIndex = existingData.findIndex((item) => item.productTitle === currentTitle);

    if (duplicateIndex !== -1) {
      existingData[duplicateIndex] = {
        ...existingData[duplicateIndex],
        ...currentProduct,
      };
      this.saveData(existingData);
      return;
    }

    const updatedData = [currentProduct, ...existingData];

    if (updatedData.length > this.maxProducts) {
      updatedData.splice(this.maxProducts);
    }

    this.saveData(updatedData);
  }

  /**
   * Generate HTML for recently viewed products
   */
  generateProductHTML(product) {
    const escapedTitle = this.escapeHtml(product.productTitle || '');
    const escapedUrl = this.escapeHtml(product.productUrl || '');
    const imageUrl = this.getProductImageUrl(product);
    const src = this.escapeHtml(this.resizeShopifyImage(imageUrl, 512));
    const src256 = this.escapeHtml(this.resizeShopifyImage(imageUrl, 256));
    const src512 = this.escapeHtml(this.resizeShopifyImage(imageUrl, 512));
    const src768 = this.escapeHtml(this.resizeShopifyImage(imageUrl, 768));
    const imageTag = src
      ? `<img src="${src}" srcset="${src256} 256w, ${src512} 512w, ${src768} 768w" sizes="(min-width: 768px) 256px, 33vw" alt="${escapedTitle}" width="256" height="358" loading="lazy" draggable="false">`
      : '';

    return `
      <div>
        <product-card class="w-full block">
          <a class="contents" href="${escapedUrl}" title="${escapedTitle}" aria-label="${escapedTitle}">
            <div class="product-media-container media-fit product-media-container--image">
              <div class="product-media">
                ${imageTag}
              </div>
            </div>
          </a>
        </product-card>
      </div>
    `;
  }

  /**
   * Render recently viewed products to the page
   */
  renderProducts() {
    const products = this.getStoredData();

    if (!products.length) {
      return;
    }

    const limitedProducts = products.slice(0, this.maxProducts);

    // Reveal the section before measuring / initializing Swiper
    const recentlyViewedElement = document.querySelector('recently-viewed');
    if (recentlyViewedElement) {
      recentlyViewedElement.setAttribute('data-has-recently-viewed', 'true');
      const section = recentlyViewedElement.closest('section');
      if (section) {
        section.setAttribute('data-has-recently-viewed', 'true');
      }
    }

    const contentEls = document.querySelectorAll('recently-viewed .slider-content');
    contentEls.forEach((el) => this.renderContainer(el, limitedProducts));

    this.initializeSliders();
  }

  /**
   * Render products in container
   */
  renderContainer(element, products) {
    element.innerHTML = products.map((product) => this.generateProductHTML(product)).join('');
  }

  /**
   * Hand off to theme SliderBlock after products are in the DOM
   */
  initializeSliders() {
    const sliderBlocks = document.querySelectorAll('recently-viewed [data-slider-block]');
    if (sliderBlocks.length === 0) return;

    const tryInit = () => {
      if (typeof window.SliderBlock !== 'function') {
        setTimeout(tryInit, 50);
        return;
      }

      sliderBlocks.forEach((block) => {
        new window.SliderBlock(block);
      });
    };

    // Wait two frames so the section is visible and laid out before Swiper measures
    requestAnimationFrame(() => {
      requestAnimationFrame(tryInit);
    });
  }

  /**
   * Initialize the recently viewed functionality
   */
  init() {
    this.addCurrentProduct();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.renderProducts());
    } else {
      this.renderProducts();
    }
  }
}

new RecentlyViewed();
